import { useOnUpdateOnly } from 'hooks/UseOnUpdateOnly';

import { areasRepository } from 'repositories/AreasRepository';
import { isOk } from 'domains/Result';
import { separateValues } from 'utils/CombineAndSeparateValues';
import { fireToast } from 'helpers/Toasts';

import { LEVELS } from 'features/location/Location.typed';
import type { useLocationProps } from 'features/location/Location.typed';
import type { CountyData } from 'features/location/Location.typed';
import {
  DEFAULT_PRESELECT,
  GROUP_NAME,
} from 'features/location/Location.constants';
import type { ListItem } from 'components/Toolkit/Inputs/CustomSelect';
import { boldMatchingText } from 'helpers/formatting';

function useLocation(props: useLocationProps) {
  const {
    level,
    isLocationBottomSheetOpen,
    isExpanded,
    selectedCounty,
    setIsLoading,
    setAutocompleteOptions,
    setCountyAreas,
    countyAreaSelect,
    levelOneReset,
    levelTwoReset,
    handleClose,
    setAreas,
    searchInput,
    locationDisplay,
    countyTown,
    countyList,
  } = props;
  // MAPPERS
  const mapAreas = (locations: ListItem[], matchingDisplayName?: string) => {
    const mappedAreas = locations.map((location) => {
      const formattedDisplayName =
        matchingDisplayName &&
        location.displayName.includes(matchingDisplayName)
          ? boldMatchingText(location.displayName, matchingDisplayName)
          : undefined;

      return {
        value: location.value,
        displayName: location.displayName,
        group: GROUP_NAME,
        formattedDisplayName,
      };
    });

    return mappedAreas;
  };

  // DATA-FETCHING
  const getAreasAutocomplete = async (term: string) => {
    setIsLoading(true);
    const areas = await areasRepository.getAreasAutocomplete(term);
    if (isOk(areas)) {
      setAutocompleteOptions(areas.ok);
    } else {
      fireToast({
        type: 'ERROR',
        text: 'Oops! Something went wrong, please try again later',
      });
      setIsLoading(false);
    }
  };

  const handleCountySelect = async (countyData: CountyData) => {
    setIsLoading(true);
    const areas = await areasRepository.getCountyAreas(
      countyData.value,
      countyData.displayName,
    );
    if (isOk(areas)) {
      const selectedCounty = { ...countyData, group: GROUP_NAME };
      // If a town is currently selected, bold that option if it is available in the area list
      const matchingValue =
        countyTown && locationDisplay?.includes(countyData.displayName);

      const updatedLocationDisplay = matchingValue
        ? locationDisplay
        : countyData.displayName;

      const mappedAreas = matchingValue
        ? mapAreas(areas.ok, locationDisplay)
        : areas.ok;

      const updatedAreas = [
        {
          value: DEFAULT_PRESELECT.value,
          displayName: 'All Counties',
          group: GROUP_NAME,
          displayBackIcon: true,
          formattedDisplayName: '<b>All Counties</b>',
        },
        {
          ...countyData,
          formattedDisplayName: matchingValue
            ? `${countyData.displayName} (All Areas)`
            : `<b>${countyData.displayName} (All Areas)</b>`,
          value: `&&&${countyData.value}&${countyData.displayName}`,
          group: GROUP_NAME,
        },
        ...mappedAreas,
      ];
      setCountyAreas(
        selectedCounty,
        updatedLocationDisplay,
        updatedAreas,
        matchingValue ? countyTown : null,
      );
    } else {
      fireToast({
        type: 'ERROR',
        text: 'Oops! Something went wrong, please try again later',
      });
      setIsLoading(false);
    }
  };

  // EVENT HANDLERS
  const handleCountyAreaSelect = (countyData: CountyData) => {
    const [latitude, longitude, countyTown, county, countyDisplayName] =
      separateValues(countyData.value);
    countyAreaSelect(
      `${countyData.displayName}`,
      {
        latitude,
        longitude,
      },
      {
        value: county,
        displayName: countyDisplayName,
        group: GROUP_NAME,
      },
      countyTown || null,
    );
  };

  const handleSelectListItem = (countyData: CountyData) => {
    switch (level) {
      case LEVELS.LEVEL_ONE:
        if (countyData.value === DEFAULT_PRESELECT.value) {
          handleReset(level);
        } else {
          handleCountySelect(countyData);
        }
        return;
      case LEVELS.LEVEL_TWO:
        if (countyData.value === DEFAULT_PRESELECT.value) {
          handleReset(level);
        } else {
          handleCountyAreaSelect(countyData);
        }
        return;
      default:
        return;
    }
  };

  // RESETING INPUT
  const handleReset = (level: LEVELS) => {
    switch (level) {
      case LEVELS.LEVEL_ONE:
        levelOneReset();
        return;
      case LEVELS.LEVEL_TWO:
        const mappedAreas = mapAreas(countyList, selectedCounty?.displayName);
        levelTwoReset(mappedAreas);
        return;
      default:
        return;
    }
  };

  const clearAreaList = () => {
    const mappedAreas = mapAreas(
      countyList,
      selectedCounty?.displayName || DEFAULT_PRESELECT.displayName,
    );
    handleClose(mappedAreas);
  };

  useOnUpdateOnly(() => {
    if (!isLocationBottomSheetOpen) {
      clearAreaList();
    }
  }, [isLocationBottomSheetOpen]);

  useOnUpdateOnly(() => {
    if (!isExpanded) {
      clearAreaList();
    }
  }, [isExpanded]);

  // HANDLE SEARCH INPUT
  useOnUpdateOnly(() => {
    switch (searchInput.length) {
      case 0:
        clearAreaList();
        return;
      case 1:
      case 2: {
        setAreas([]);
        return;
      }
      default:
        getAreasAutocomplete(searchInput);
        return;
    }
  }, [searchInput]);

  return {
    getAreasAutocomplete,
    handleSelectListItem,
    handleReset,
    handleCountySelect,
    handleCountyAreaSelect,
    clearAreaList,
  };
}

export { useLocation };
